/**
 * Flythrough store — playlists, scoped per surface, plus transient playback state.
 *
 * Playlists are keyed by an opaque `scope` string supplied by the mounting page
 * (`ramp:<locationId>`, `event-planner:<locationId>`, `cinematic:<sceneId>`), so
 * tours authored at one location never show up at another and the module itself
 * stays ignorant of what a "location" is.
 *
 * Only `byScope` is persisted. Playback state is deliberately excluded — a
 * reload must never come back mid-tour with the camera locked and the UI hidden.
 */
import { create } from 'zustand'
import { devtools, persist } from 'zustand/middleware'
import { makeId } from './schema'
import type { FlythroughPlaylist, FlythroughWaypoint, CameraPose } from './types'

const PERSIST_KEY = 'planmax-flythrough-v1'

const DEFAULT_DURATION_MS = 3000

interface ScopeState {
  playlists: FlythroughPlaylist[]
  activeId: string | null
}

interface FlythroughState {
  byScope: Record<string, ScopeState>

  // ── Transient playback (never persisted) ────────────────────────────────
  isPlaying: boolean
  /** Scope whose playlist is currently flying — guards against cross-mount bleed. */
  playingScope: string | null
  /** Progress in ms through the compiled tour; written ~10x/sec for the UI. */
  elapsedMs: number
  /** Total tour length, published once at play time so the scrubber can size itself. */
  totalMs: number

  // ── Playlist CRUD ───────────────────────────────────────────────────────
  getScope: (scope: string) => ScopeState
  addPlaylist: (scope: string, name?: string) => string
  loadScope: (scope: string, playlists: FlythroughPlaylist[]) => void
  renamePlaylist: (scope: string, id: string, name: string) => void
  deletePlaylist: (scope: string, id: string) => void
  setActivePlaylist: (scope: string, id: string | null) => void
  updatePlaylist: (scope: string, id: string, patch: Partial<FlythroughPlaylist>) => void
  importPlaylists: (scope: string, playlists: FlythroughPlaylist[]) => void

  // ── Waypoint CRUD ───────────────────────────────────────────────────────
  captureWaypoint: (scope: string, pose: CameraPose) => void
  updateWaypoint: (scope: string, wpId: string, patch: Partial<FlythroughWaypoint>) => void
  deleteWaypoint: (scope: string, wpId: string) => void
  moveWaypoint: (scope: string, from: number, to: number) => void
  setDurations: (scope: string, durations: number[]) => void

  // ── Playback ────────────────────────────────────────────────────────────
  beginPlayback: (scope: string, totalMs: number) => void
  endPlayback: () => void
  setElapsed: (ms: number) => void
}

const EMPTY_SCOPE: ScopeState = { playlists: [], activeId: null }

/** Apply `fn` to a scope's state, creating it if absent. */
function withScope(
  state: FlythroughState,
  scope: string,
  fn: (s: ScopeState) => ScopeState
): Pick<FlythroughState, 'byScope'> {
  const current = state.byScope[scope] ?? EMPTY_SCOPE
  return { byScope: { ...state.byScope, [scope]: fn(current) } }
}

/** Apply `fn` to the scope's active playlist. No-op when there isn't one. */
function withActivePlaylist(
  state: FlythroughState,
  scope: string,
  fn: (p: FlythroughPlaylist) => FlythroughPlaylist
): Pick<FlythroughState, 'byScope'> {
  return withScope(state, scope, (s) => {
    if (!s.activeId) return s
    return { ...s, playlists: s.playlists.map((p) => (p.id === s.activeId ? fn(p) : p)) }
  })
}

export function createPlaylist(name: string): FlythroughPlaylist {
  return {
    id: makeId('fly'),
    name,
    waypoints: [],
    loop: false,
    hideUi: true,
    rampMs: 800,
    speed: 1,
    restoreCameraOnStop: true,
  }
}

export const useFlythroughStore = create<FlythroughState>()(
  devtools(
    persist(
      (set, get) => ({
        byScope: {},
        isPlaying: false,
        playingScope: null,
        elapsedMs: 0,
        totalMs: 0,

        getScope: (scope) => get().byScope[scope] ?? EMPTY_SCOPE,

        addPlaylist: (scope, name) => {
          const existing = (get().byScope[scope] ?? EMPTY_SCOPE).playlists.length
          const playlist = createPlaylist(name ?? `Tour ${existing + 1}`)
          set(
            (s) => withScope(s, scope, (sc) => ({
              playlists: [...sc.playlists, playlist],
              activeId: playlist.id,
            })),
            false,
            'flythrough/addPlaylist'
          )
          return playlist.id
        },

        loadScope: (scope, playlists) =>
          set(
            (s) => withScope(s, scope, () => ({
              playlists,
              activeId: playlists[0]?.id ?? null,
            })),
            false,
            'flythrough/loadScope'
          ),

        renamePlaylist: (scope, id, name) =>
          set(
            (s) => withScope(s, scope, (sc) => ({
              ...sc,
              playlists: sc.playlists.map((p) => (p.id === id ? { ...p, name } : p)),
            })),
            false,
            'flythrough/renamePlaylist'
          ),

        deletePlaylist: (scope, id) =>
          set(
            (s) => withScope(s, scope, (sc) => {
              const playlists = sc.playlists.filter((p) => p.id !== id)
              return {
                playlists,
                activeId: sc.activeId === id ? (playlists[0]?.id ?? null) : sc.activeId,
              }
            }),
            false,
            'flythrough/deletePlaylist'
          ),

        setActivePlaylist: (scope, id) =>
          set((s) => withScope(s, scope, (sc) => ({ ...sc, activeId: id })), false, 'flythrough/setActive'),

        updatePlaylist: (scope, id, patch) =>
          set(
            (s) => withScope(s, scope, (sc) => ({
              ...sc,
              playlists: sc.playlists.map((p) => (p.id === id ? { ...p, ...patch } : p)),
            })),
            false,
            'flythrough/updatePlaylist'
          ),

        importPlaylists: (scope, playlists) =>
          set(
            (s) => withScope(s, scope, (sc) => ({
              playlists: [...sc.playlists, ...playlists],
              activeId: playlists[0]?.id ?? sc.activeId,
            })),
            false,
            'flythrough/import'
          ),

        captureWaypoint: (scope, pose) =>
          set(
            (s) => withActivePlaylist(s, scope, (p) => ({
              ...p,
              waypoints: [
                ...p.waypoints,
                {
                  id: makeId('wp'),
                  name: `Shot ${p.waypoints.length + 1}`,
                  center: [pose.center[0], pose.center[1]],
                  zoom: pose.zoom,
                  pitch: pose.pitch,
                  bearing: pose.bearing,
                  durationMs: DEFAULT_DURATION_MS,
                  holdMs: 0,
                },
              ],
            })),
            false,
            'flythrough/captureWaypoint'
          ),

        updateWaypoint: (scope, wpId, patch) =>
          set(
            (s) => withActivePlaylist(s, scope, (p) => ({
              ...p,
              waypoints: p.waypoints.map((w) => (w.id === wpId ? { ...w, ...patch } : w)),
            })),
            false,
            'flythrough/updateWaypoint'
          ),

        deleteWaypoint: (scope, wpId) =>
          set(
            (s) => withActivePlaylist(s, scope, (p) => ({
              ...p,
              waypoints: p.waypoints.filter((w) => w.id !== wpId),
            })),
            false,
            'flythrough/deleteWaypoint'
          ),

        moveWaypoint: (scope, from, to) =>
          set(
            (s) => withActivePlaylist(s, scope, (p) => {
              if (from === to) return p
              const waypoints = [...p.waypoints]
              if (from < 0 || from >= waypoints.length) return p
              const clampedTo = Math.max(0, Math.min(waypoints.length - 1, to))
              const [moved] = waypoints.splice(from, 1)
              waypoints.splice(clampedTo, 0, moved)
              return { ...p, waypoints }
            }),
            false,
            'flythrough/moveWaypoint'
          ),

        setDurations: (scope, durations) =>
          set(
            (s) => withActivePlaylist(s, scope, (p) => ({
              ...p,
              waypoints: p.waypoints.map((w, i) => ({ ...w, durationMs: durations[i] ?? w.durationMs })),
            })),
            false,
            'flythrough/setDurations'
          ),

        beginPlayback: (scope, totalMs) =>
          set({ isPlaying: true, playingScope: scope, elapsedMs: 0, totalMs }, false, 'flythrough/begin'),

        endPlayback: () =>
          set({ isPlaying: false, playingScope: null, elapsedMs: 0 }, false, 'flythrough/end'),

        setElapsed: (ms) => set({ elapsedMs: ms }, false, 'flythrough/elapsed'),
      }),
      {
        name: PERSIST_KEY,
        // Transient playback state must never survive a reload.
        partialize: (state) => ({ byScope: state.byScope }),
      }
    ),
    { name: 'FlythroughStore' }
  )
)

/** Convenience selector — the active playlist for a scope, or null. */
export function selectActivePlaylist(
  state: FlythroughState,
  scope: string
): FlythroughPlaylist | null {
  const sc = state.byScope[scope]
  if (!sc?.activeId) return null
  return sc.playlists.find((p) => p.id === sc.activeId) ?? null
}
